import * as THREE from 'three';
import { enqueueSnackbar } from 'notistack';
import { ISceneManagerContext } from '../hooks/SceneManagerProvider/sceneManagerContext';
import { Model } from '../hooks/useModelState';
import { SceneRegister } from './types';
import * as PCConstants from '../MineModel/PCConstants';
import { applyIsolateFilters } from './StopeSelectedScene/StopeSelectedSceneActions';
import { createRoutesConstraintFilter } from '../MineModel/Filters/Filters';
import { PCNode } from '../MineModel/Nodes';
import { RoutePoint } from '../MineModel/types';
import PCRoute from '../MineModel/Route/PCRoute';
import PCSpool from '../MineModel/Spools/PCSpool';
import { generateAllRoutes, routeOptsForProject } from '../MineModel/Route/utils';
import { describeRouteFailure, routeFailureKey } from '../MineModel/Route/errors';
import { resetStopeFillSceneState } from './StopeFillScene/stopeFillSceneState';
import { resetBulkImportSolidsSceneState } from './BulkImportSolidsScene/bulkImportSolidsSceneState';
import { resetBulkEditSceneState } from './BulkEditScene/bulkEditSceneState';
import { resetFlushSceneState } from '../MineModel/Flushing/flushSceneState';

interface Coords {
  x: number;
  y: number;
  z: number;
}

export const cadToThreeJsPosition = (coords: Coords): THREE.Vector3 => new THREE.Vector3(-coords.x, coords.z, coords.y);

export const cadToThreeJsPositionWithOffset = (coords: Coords, offset: THREE.Vector3, scale: number): [number, number, number] => [
    (-coords.x + offset.x) * scale,
    (coords.z + offset.y) * scale,
    (coords.y + offset.z) * scale,
  ];

export const threeJsPositionToCad = (threeJsPos: THREE.Vector3, offset: THREE.Vector3, scale: number): Coords => {
  const cadX = -((threeJsPos.x / scale) - offset.x);
  const cadY = (threeJsPos.z / scale) - offset.z;
  const cadZ = (threeJsPos.y / scale) - offset.y;
  return { x: cadX, y: cadY, z: cadZ };
};

export interface SceneSideDrawerRoute {
  path: string;
  backNavPath?: string;
  backNavText?: string;
  onBack?: () => void;
  isOpen: boolean;
  component: JSX.Element;
  setIsOpen: (isOpen: boolean) => void;
  authenticated?: () => boolean;
}

export interface Meta {
  path: string;
  url: string;
}

export interface SceneProps {
  key: SceneRegister;
  meta: Meta;
  context: ISceneManagerContext;
}

let constraintNodeIds: string[] = [];
let constraintSpoolIds: string[] = [];

type NormalisedPathParts = {
  parts: string[];
  overviewIndex: number;
};

const normalisePath = (pathname: string): NormalisedPathParts => {
  const parts = pathname.split('/').filter(Boolean);
  const normalisedParts = parts[0] === 'app' ? parts.slice(1) : parts;
  const overviewIndex = normalisedParts.findIndex((part) => part === 'overview');
  return { parts: normalisedParts, overviewIndex };
};

const buildBasePath = (pathname: string): string => {
  const { parts, overviewIndex } = normalisePath(pathname);
  if (overviewIndex === -1) {
    return pathname.startsWith('/') ? pathname : `/${pathname}`;
  }
  const baseSegments = parts.slice(0, overviewIndex + 1);
  return `/${baseSegments.join('/')}`;
};

export const withCurrentSearch = (path: string): string => `${path}${window.location.search}`;

const flattenStopes = (model: Model) => (
  Object.values(model.stopes || {}).flatMap((arr) => arr ?? [])
);

const findStopeById = (model: Model, stopeId: string) => (
  flattenStopes(model).find((stope: any) => stope?.data?.stopeId === stopeId)
);

export const resetNodeColours = (model: Model, id: string) => {
  model.setNodeSize(id, PCConstants.NODE_RADIUS);
  model.setNodeColour(id, PCConstants.NODE_COLOUR);
  model.setNodeConstraint(id, false);
};

export const setNodeColours = (model: Model, id: string) => {
  model.setNodeSize(id, PCConstants.NODE_RADIUS * 1.5);
  model.setNodeColour(id, PCConstants.STOPE_COLOUR);
  model.setNodeConstraint(id, true);
};

export const applyConstraintFilter = (model: Model) => {
  model.addFilter('routes', 'routeConstraints', (routes) => createRoutesConstraintFilter(constraintNodeIds, constraintSpoolIds)(routes));
  applyIsolateFilters(model);
  model.setSelectedRoute(undefined);
};

export const toggleConstraint = (model: Model, nodeId: string | undefined) => {
  if (!nodeId) return;
  if (constraintNodeIds.includes(nodeId)) {
    constraintNodeIds = constraintNodeIds.filter((id) => id !== nodeId);
    resetNodeColours(model, nodeId);
  } else {
    constraintNodeIds = [...constraintNodeIds, nodeId];
    setNodeColours(model, nodeId);
  }
  applyConstraintFilter(model);
};

export const toggleSpoolConstraint = (model: Model, spool: PCSpool | undefined) => {
  if (!spool) return;
  const { spoolId } = spool;
  if (constraintSpoolIds.includes(spoolId)) {
    constraintSpoolIds = constraintSpoolIds.filter((id) => id !== spoolId);
    model.setSpoolConstraint(spoolId, false);
  } else {
    constraintSpoolIds = [...constraintSpoolIds, spoolId];
    model.setSpoolConstraint(spoolId, true);
  }
  applyConstraintFilter(model);
};

export const resetConstraintNodes = (model: Model) => {
  constraintNodeIds.forEach((id) => {
    resetNodeColours(model, id);
  });
  constraintNodeIds = [];
  constraintSpoolIds.forEach((id) => {
    model.setSpoolConstraint(id, false);
  });
  constraintSpoolIds = [];
  applyConstraintFilter(model);
};

export interface ResolveSceneResult {
  scene: SceneRegister;
  stopeId?: string;
}

export const resolveSceneFromPath = (pathname: string): ResolveSceneResult => {
  const { parts, overviewIndex } = normalisePath(pathname);
  if (overviewIndex === -1) {
    return { scene: 'DEFAULT' };
  }

  const afterOverview = parts.slice(overviewIndex + 1);
  if (afterOverview.length === 0) {
    return { scene: 'DEFAULT' };
  }

  const [first, second] = afterOverview;

  if (first === 'stopes') {
    if (!second) {
      return { scene: 'DEFAULT' };
    }

    if (second === 'manual-nodes') {
      return { scene: 'MANUEL_NODES' };
    }

    if (second === 'wear-monitoring') {
      return { scene: 'WEAR_MONITORING' };
    }

    if (second === 'bulk-import-solids') {
      return { scene: 'BULK_IMPORT_SOLIDS' };
    }

    if (second === 'bulk-edit') {
      return { scene: 'BULK_EDIT' };
    }

    if (second === 'scenario-manager') {
      return { scene: 'SCENARIO_MANAGER' };
    }

    const [, third] = afterOverview.slice(1);
    if (third === 'fill') {
      return { scene: 'STOPE_FILL', stopeId: second };
    }

    return { scene: 'STOPE_SELECTED', stopeId: second };
  }

  if (first === 'wear-monitoring') {
    return { scene: 'WEAR_MONITORING' };
  }

  return { scene: 'DEFAULT' };
};

interface PrepareSceneOptions {
  model: Model;
  basePath: string;
  stopeId?: string;
  stope?: any;
  readOnly?: boolean;
}

export interface ScenePreparationResult {
  scene: SceneRegister;
  targetPath?: string;
  deferred?: boolean;
}

export const prepareSceneState = (
  requestedScene: SceneRegister,
  { model, basePath, stopeId, stope, readOnly }: PrepareSceneOptions,
): ScenePreparationResult => {
  let scene: SceneRegister = requestedScene;
  let targetPath: string | undefined;
  let deferred = false;

  if (requestedScene !== 'STOPE_FILL') {
    resetStopeFillSceneState();
  }

  if (requestedScene !== 'BULK_IMPORT_SOLIDS') {
    resetBulkImportSolidsSceneState();
  }

  if (requestedScene !== 'BULK_EDIT') {
    resetBulkEditSceneState();
  }

  if (requestedScene !== 'STOPE_SELECTED') {
    resetFlushSceneState();
  }

  switch (requestedScene) {
    case 'MANUEL_NODES': {
      model.setRoutes({});
      model.setSelectedStope(undefined);
      resetConstraintNodes(model);
      targetPath = `${basePath}/stopes/manual-nodes`;
      break;
    }
    case 'STOPE_SELECTED': {
      const stopeToSelect = stopeId
        ? (stope ?? findStopeById(model, stopeId))
        : undefined;

      if (stopeToSelect) {
        const currentStopeId = model.selectedStope?.data?.stopeId;
        if (currentStopeId !== stopeToSelect.data?.stopeId) {
          model.setSelectedStope(stopeToSelect);
        }
        targetPath = `${basePath}/stopes/${stopeToSelect.data?.stopeId}`;
      } else if (readOnly) {
        deferred = true;
        break;
      } else {
        model.setRoutes({});
        model.setSelectedStope(undefined);
        resetConstraintNodes(model);
        scene = 'DEFAULT';
        targetPath = `${basePath}/stopes`;
      }
      break;
    }
    case 'STOPE_FILL': {
      const stopeToSelect = stopeId
        ? (stope ?? findStopeById(model, stopeId))
        : undefined;

      if (stopeToSelect) {
        const currentStopeId = model.selectedStope?.data?.stopeId;
        if (currentStopeId !== stopeToSelect.data?.stopeId) {
          model.setSelectedStope(stopeToSelect);
        }
        targetPath = `${basePath}/stopes/${stopeToSelect.data?.stopeId}/fill`;
      } else if (readOnly) {
        deferred = true;
        break;
      } else {
        model.setRoutes({});
        model.setSelectedStope(undefined);
        resetConstraintNodes(model);
        scene = 'DEFAULT';
        targetPath = `${basePath}/stopes`;
      }
      break;
    }
    case 'BULK_IMPORT_SOLIDS': {
      model.setRoutes({});
      model.setSelectedStope(undefined);
      resetConstraintNodes(model);
      targetPath = `${basePath}/stopes/bulk-import-solids`;
      break;
    }
    case 'WEAR_MONITORING': {
      model.setRoutes({});
      model.setSelectedStope(undefined);
      resetConstraintNodes(model);
      targetPath = `${basePath}/stopes/wear-monitoring`;
      break;
    }
    case 'BULK_EDIT': {
      model.setRoutes({});
      model.setSelectedStope(undefined);
      resetConstraintNodes(model);
      targetPath = `${basePath}/stopes/bulk-edit`;
      break;
    }
    case 'SCENARIO_MANAGER': {
      model.setRoutes({});
      model.setSelectedStope(undefined);
      resetConstraintNodes(model);
      targetPath = `${basePath}/stopes/scenario-manager`;
      break;
    }
    default: {
      model.setSelectedStope(undefined);
      targetPath = `${basePath}/stopes`;
    }
  }

  return { scene, targetPath, deferred };
};

interface NavigateSceneOptions {
  model: Model;
  context: ISceneManagerContext;
  stopeId?: string;
  stope?: any;
  replace?: boolean;
  basePath?: string;
}

export const navigateToScene = (
  requestedScene: SceneRegister,
  {
    model,
    context,
    stopeId,
    stope,
    replace = true,
    basePath,
  }: NavigateSceneOptions,
) => {
  const resolvedBasePath = basePath ?? buildBasePath(window.location.pathname);
  const { scene, targetPath } = prepareSceneState(requestedScene, {
    model,
    basePath: resolvedBasePath,
    stopeId,
    stope,
  });

  context.actions.setScene(scene, model);

  if (targetPath) {
    context.actions.navigate(withCurrentSearch(targetPath), { replace });
  }

  return { scene, targetPath };
};

export const syncSceneFromPath = (
  pathname: string,
  model: Model,
  context: ISceneManagerContext,
) => {
  const { scene: requestedScene, stopeId } = resolveSceneFromPath(pathname);

  if (context.state.currentScene?.key === requestedScene && !stopeId) {
    return;
  }

  const basePath = buildBasePath(pathname);
  const { scene, deferred } = prepareSceneState(requestedScene, {
    model,
    basePath,
    stopeId,
    readOnly: true,
  });
  if (!deferred && context.state.currentScene?.key !== scene) {
    context.actions.setScene(scene, model);
  }
};

export const handleStopeSelect = (
  model: Model,
  node: PCNode,
  props?: SceneProps,
  stopeId?: string,
) => {
  const allStopes = Object.values(model.stopes || {}).flatMap((arr: any[]) => arr);
  const stope = stopeId
    ? allStopes.find((s: any) => s?.data?.stopeId === stopeId)
    : allStopes.find((s: any) => s?.originNode?.id === node.id);

  if (stope) {
    resetConstraintNodes(model);
    if (props) {
      navigateToScene('STOPE_SELECTED', {
        model,
        context: props.context,
        stopeId: stope.data?.stopeId,
        stope,
      });
    } else {
      model.setSelectedStope(stope);
    }
  }
};

export const handleNodeSelect = (
  model: Model,
  node: PCNode,
  props: SceneProps,
  stopeId?: string,
) => {
  model.setSelectedNode(node);
  if (node.isStope) {
    handleStopeSelect(model, node, props, stopeId);
    return;
  }
  if (node.isPlantNode) {
    model.setActiveBackfillPlant(node);
    resetConstraintNodes(model);
    if (model.selectedStope?.originNode) {
      try {
        const { routeObjects: routes } = generateAllRoutes(
          node,
          model.spools,
          model.selectedStope,
          Object.values(model.pipes),
          routeOptsForProject(model.project),
          model.nodes,
          model.pipes,
        );
        model.setRoutes(routes);
      } catch (error) {
        model.setRoutes({});
        enqueueSnackbar(describeRouteFailure(error), { variant: 'error', key: routeFailureKey(error) });
      }
    } else {
      model.setRoutes({});
    }
    return;
  }
  const nodeOnAnyRoute = Object.values(model.routes || {}).some((route: PCRoute) => route?.routePoints?.some((p: RoutePoint) => p.point.id === node.id));
  if (nodeOnAnyRoute) {
    toggleConstraint(model, node?.id);
  }
};

export const handleNavigateToSelectedStopeScene = (
  model: Model,
  node: PCNode,
  props: SceneProps,
  stopeId?: string,
) => {
  const allStopes = Object.values(model.stopes).flatMap((arr) => arr ?? []);
  const stope = stopeId
    ? allStopes.find((s) => s?.data?.stopeId === stopeId)
    : allStopes.find((s) => s?.originNode?.id === node?.id);
  if (stope) {
    resetConstraintNodes(model);
    navigateToScene('STOPE_SELECTED', {
      model,
      context: props.context,
      stopeId: stope.data?.stopeId,
      stope,
    });
  }
};

export const handleNavigateToManuelNodesScene = (
  model: Model,
  props: SceneProps,
) => {
  navigateToScene('MANUEL_NODES', {
    model,
    context: props.context,
  });
};

export const handleNavigateToDefaultScene = (
  model: Model,
  props: SceneProps,
) => {
  navigateToScene('DEFAULT', {
    model,
    context: props.context,
  });
};

export const handleNavigateToStopeFillScene = (
  model: Model,
  props: SceneProps,
  stopeId?: string,
) => {
  navigateToScene('STOPE_FILL', {
    model,
    context: props.context,
    stopeId: stopeId ?? model.selectedStope?.data?.stopeId,
  });
};

export const handleNavigateToBulkImportSolidsScene = (
  model: Model,
  props: SceneProps,
) => {
  navigateToScene('BULK_IMPORT_SOLIDS', {
    model,
    context: props.context,
  });
};

export const handleNavigateToBulkEditScene = (
  model: Model,
  props: SceneProps,
) => {
  navigateToScene('BULK_EDIT', {
    model,
    context: props.context,
  });
};

export const handleNavigateToScenarioManagerScene = (
  model: Model,
  props: SceneProps,
) => {
  navigateToScene('SCENARIO_MANAGER', {
    model,
    context: props.context,
  });
};
